import { useCallback, useEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useAutoSavePreference } from "@/hooks/usePrescriptionLayout";
import type { PrescriptionFormData } from "@/types/prescription";
import { RX_DRAFT_PREFIX } from "@/lib/prescriptionDrafts";

/**
 * Unsaved prescriptions survive leaving the pad. While the doctor types, the form is kept on this
 * device (debounced); opening the same pad again — the same patient's new prescription, or the
 * same prescription's edit — brings it back, with Discard in the toast. Saving or cancelling drops
 * it. Drafts older than 12 hours are ignored, logging out clears them all, and Settings →
 * Preferences → Auto-save turns this off.
 */

const MAX_AGE_MS = 12 * 60 * 60 * 1000;
const DEBOUNCE_MS = 600;

type Draft = { savedAt: number; values: PrescriptionFormData };

const read = (key: string): Draft | null => {
  try {
    const d = JSON.parse(localStorage.getItem(key) || "null") as Draft | null;
    return d && Date.now() - d.savedAt < MAX_AGE_MS ? d : null;
  } catch { return null; }
};
const write = (key: string, values: PrescriptionFormData) => {
  try { localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), values })); } catch { /* storage full or blocked */ }
};
const remove = (key: string) => {
  try { localStorage.removeItem(key); } catch { /* blocked */ }
};

export function usePrescriptionDraft({ form, doctorId, mode, patientId, rxId, initialUpdatedAt, isCopy }: {
  form: UseFormReturn<PrescriptionFormData>;
  doctorId?: string | null;
  mode: "create" | "edit";
  patientId: string;
  rxId?: string;
  /** Edit mode: a draft older than the saved prescription is stale */
  initialUpdatedAt?: string;
  /** "Copy Rx" starts from another prescription; don't replace that with an older draft */
  isCopy: boolean;
}) {
  const enabled = useAutoSavePreference();
  const key = doctorId && (mode === "edit" ? rxId : patientId)
    ? `${RX_DRAFT_PREFIX}${doctorId}:${mode === "edit" ? `edit:${rxId}` : `new:${patientId}`}`
    : null;

  // Set once the draft is deliberately finished (saved / cancelled) so unmounting doesn't re-save it
  const doneRef = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Restore once, when the pad opens
  useEffect(() => {
    if (!key || isCopy) return;
    const draft = read(key);
    if (!draft) { remove(key); return; }
    if (initialUpdatedAt && new Date(initialUpdatedAt).getTime() >= draft.savedAt) { remove(key); return; }
    form.reset(draft.values, { keepDefaultValues: true });
    const at = new Date(draft.savedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    toast({
      title: "Unsaved prescription restored",
      description: `Your changes from ${at} are back on the pad.`,
      action: (
        <ToastAction altText="Discard restored changes" onClick={() => { remove(key); form.reset(); }}>
          Discard
        </ToastAction>
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Keep the draft current while typing; write any pending change when the pad closes
  useEffect(() => {
    if (!key) return;
    let timer: number | undefined;
    const flush = () => {
      window.clearTimeout(timer);
      timer = undefined;
      if (doneRef.current || !enabledRef.current) return;
      if (form.formState.isDirty) write(key, form.getValues());
      else remove(key);
    };
    const sub = form.watch(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(flush, DEBOUNCE_MS);
    });
    // Closing the tab or reloading
    window.addEventListener("pagehide", flush);
    return () => {
      sub.unsubscribe();
      window.removeEventListener("pagehide", flush);
      if (timer !== undefined) flush();
    };
  }, [key, form]);

  /** Saved or cancelled: the draft is no longer needed */
  const discardDraft = useCallback(() => {
    doneRef.current = true;
    if (key) remove(key);
  }, [key]);

  return { discardDraft };
}
